import * as React from 'react';
import { styled } from '@mui/material/styles';
import { AppProvider, Navigation, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout, SidebarFooterProps } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core';
import { BillingIcon, EasyCrmPurple, EwLogo, ExpandLeftIcon, HandshakeIcon, LogoutIcon, Notification, NotificationCancel, OverLay, ProfileIcon, SettingIcon, SideBarRingIcon, SignOut, SubscriptionIcon, UserIcon, WorkspaceIcon } from '../assets/icons';
import { AvatarImg, easyCrmLogo, ewLogo, greenTimer, Logo, pinkTimer, purpleTimer, } from '../assets/images';
import { Avatar, Box, Button, createTheme, IconButton, Menu, Paper, ThemeProvider, Typography, CircularProgress, Tooltip, MenuItem, Radio } from '@mui/material';
import Billing from '../components/Billing';
import Notifications from '../components/Notifications';
import ManageSubscription from '../components/subscription/ManageSubscription';
import Settings from '../components/Settings';
import theme from '../theme';
import Overview from '../components/overview';
import SubscriptionDetails from '../components/subscription/SubscriptionDetails';
import SubscriptionDetailsEasyCRM from '../components/subscription/SubscriptionDetailsEasyCRM';
import OverViewIcon from '../assets/icons/overviewIcon';
import AuthService from '../services/auth.service';
import { decodeIdToken, decodeMetadataToken } from '../utils/token.utils';
import { STORAGE_KEYS } from '../utils/storage.util';
import { validateUserPermissions, checkFeatureEntitlement } from '../utils/common.util';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Workspaces from '../components/Workspaces';
import Users from '../components/Users';

import Intercom from '@intercom/messenger-js-sdk';
import { isAuthenticated } from '../routes/ProtectedRoute';
import { config } from '../config';
import Loader from '../components/common/Loader';

interface DemoPageContentProps {
    pathname: string;
    navigate: (path: string | URL, options?: { state?: any }) => void;
    state?: any;
}

const Dashboard = () => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isAppLoading, setIsAppLoading] = React.useState(false);
    const [isEasyWebinarLoading, setIsEasyWebinarLoading] = React.useState(false);
    const [isRedirecting, setIsRedirecting] = React.useState(false);
    const navigate = useNavigate();
    
    // Check CRM entitlement once
    const crmEntitlement = React.useMemo(() => checkFeatureEntitlement('easycrm'), []);

    const handleSignOut = async () => {
        navigate('/logout');
    };

    const metadata = decodeMetadataToken();
    const decodedToken = decodeIdToken();

    const handleEasyWebinarClick = async () => {
        try {
            // Set loading state for just the EasyWebinar button
            setIsEasyWebinarLoading(true);
            
            // First refresh the token to ensure we have the latest subscription data
            await AuthService.refreshToken();
            
            // Check subscription status in metadata token
            const metadata = decodeMetadataToken();
            const subscriptionStatus = metadata?.companies?.[0]?.subscription?.status;
            
            if (subscriptionStatus === 'cancelled') {
                // Show error message if subscription is cancelled
                toast.error('Your subscription has been cancelled. Please renew your subscription to access EasyWebinar.');
                setIsEasyWebinarLoading(false);
                setIsRedirecting(false);
                return;
            }
            
            // If subscription is active, proceed with navigation
            const state = localStorage.getItem(STORAGE_KEYS.STATE);
            const easyWebinarUrl = `${config.easywebinar_v2_url}/ew-auto-login${state ? `?state=${state}` : ''}`;
            window.location.href = easyWebinarUrl;
        } catch (error) {
            console.error('Error navigating to EasyWebinar:', error);
            toast.error('An error occurred. Please try again later.');
            setIsEasyWebinarLoading(false);
            setIsRedirecting(false);
        }
    };

    React.useEffect(() => {
        // Check if redirect parameter exists in URL
        const urlParams = new URLSearchParams(window.location.search);
        const redirectParam = urlParams.get('redirect');
        const subStatusParam = urlParams.get('sub_status');
        const errorParam = urlParams.get('error');
        
        // Check for sub_status parameter
        if (subStatusParam) {
            toast.warning(`Your subscription is in ${subStatusParam} state. Please activate your plan.`);
            // Clear subStatusParam from URL
            urlParams.delete('sub_status');
        }
        
        // Check for error parameter
        if (errorParam) {
            // Show specific message based on error type
            if (errorParam === 'user_disabled') {
                toast.error('Your account has been disabled. Please contact support.');
            } else if (errorParam === 'user_not_found') {
                toast.error('User account not found. Please contact support.');
            } else {
                toast.error(`${errorParam}. Please contact support.`);
            }
            // Clear errorParam from URL
            urlParams.delete('error');
        }
        
        // Update URL without the cleared parameters
        if (subStatusParam || errorParam) {
            const newUrl = urlParams.toString() 
                ? `${window.location.pathname}?${urlParams.toString()}` 
                : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
        
        if (redirectParam === 'easywebinar') {
            setIsRedirecting(true);
            handleEasyWebinarClick();
        }
        
        // console.log('decodedToken', decodedToken)
        Intercom({
            app_id: 'xea66y68',
            user_id: decodedToken?.sub,
            name: decodedToken?.name || `${decodedToken?.given_name || ''} ${decodedToken?.family_name || ''}`.trim(),
            email: decodedToken?.email,
            created_at: Date.now(),
            user_hash: metadata?.incomhash
        });
    }, [])

    const NAVIGATION: Navigation = [
        {
            icon: <OverViewIcon />,
            segment: 'overview',
            title: 'Overview',
        },
        {
            icon: <SubscriptionIcon />,
            segment: 'subscription',
            title: 'Subscription',
            pattern: 'subscription{/:orderId}*',
        },
        {
            icon: <BillingIcon />,
            segment: 'billing',
            title: 'Billing',
        },
        {
            icon: <UserIcon />,
            segment: 'users',
            title: 'Team',
        },
        {
            icon: <WorkspaceIcon />,
            segment: 'workspaces',
            title: 'Client Workspace',
        },
        // {
        //     icon: <SideBarRingIcon />,
        //     segment: 'notifications',
        //     title: 'Notifications',
        // },
        {
            icon: <SettingIcon />,
            segment: 'settings',
            title: 'Settings',
        },
    ];

    const companies = metadata?.companies
    if(companies && companies.length > 0) {
        const role = companies[0]?.applicationRoles['EASYWEBINAR']
        if(role !== 'OWNER') {
            // Remove subscription, billing, and users for non-owners
            NAVIGATION.splice(1, 3);
        }
    
        if(companies[0].parentCompanyId) {
            // Remove workspaces, subscription and billing for child companies
            const workspacesIndex = NAVIGATION.findIndex(nav => 'segment' in nav && nav.segment === 'workspaces');
            if (workspacesIndex !== -1) {
                NAVIGATION.splice(workspacesIndex, 1);
            }
            
            const subscriptionIndex = NAVIGATION.findIndex(nav => 'segment' in nav && nav.segment === 'subscription');
            if (subscriptionIndex !== -1) {
                NAVIGATION.splice(subscriptionIndex, 1);
            }
            
            const billingIndex = NAVIGATION.findIndex(nav => 'segment' in nav && nav.segment === 'billing');
            if (billingIndex !== -1) {
                NAVIGATION.splice(billingIndex, 1);
            }
        }
    }

    const useDemoRouter = (initialPath: string) => {
        const [pathname, setPathname] = React.useState(initialPath);
        const [state, setState] = React.useState<any>(null);

        const router = React.useMemo(() => {
            if (!isAuthenticated()) {
                window.location.href = '/logout'
            }
            
            return {
                pathname,
                searchParams: new URLSearchParams(),
                state,
                navigate: (path: string | URL, options?: { state?: any }) => {
                    setPathname(String(path));
                    if (options?.state) {
                        setState(options.state);
                    }
                    // Update the browser URL when navigating
                    const newPath = `/dashboard${path}`;
                    window.history.pushState({}, '', newPath);
                },
            };
        }, [pathname, state]);

        return router;
    };

    function ToolbarActionsSearch({ navigate }: { navigate: (path: string, options?: { state?: any }) => void }) {
        const userInfo = React.useMemo(() => decodeIdToken(), []);
        const [anchorElNotification, setAnchorElNotification] = React.useState<null | HTMLElement>(null);
        const [anchorElOverlay, setAnchorElOverlay] = React.useState<null | HTMLElement>(null);
        const [anchorElProfile, setAnchorElProfile] = React.useState<null | HTMLElement>(null);

        const handleMenuOpenNotification = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorElNotification(event.currentTarget);
        };

        const handleMenuCloseNotification = () => {
            setAnchorElNotification(null);
        };

        const handleMenuOpenOverlay = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorElOverlay(event.currentTarget);
        };

        const handleMenuCloseOverlay = () => {
            setAnchorElOverlay(null);
        };

        const handleMenuOpenProfile = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorElProfile(event.currentTarget);
        };

        const handleMenuCloseProfile = () => {
            setAnchorElProfile(null);
        };

        const handleManageProfile = () => {
            navigate('/settings', { state: { activeTab: 0 } });
            handleMenuCloseProfile();
        }

        return (
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* <IconButton onClick={handleMenuOpenNotification} sx={{ "& svg rect:hover": { fill: '#f2f2f2' }, "& svg:hover path": { fill: '#178EFC' }, padding: '0', marginRight: "12px" }}>
                            <Notification />
                        </IconButton> */}
                        <Menu
                            anchorEl={anchorElNotification}
                            open={Boolean(anchorElNotification)}
                            onClose={handleMenuCloseNotification}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            sx={{ '& .MuiMenu-paper': { borderRadius: "12px !important", boxShadow: "0px 12px 20px 0px #0D0E213D" } }}
                        >
                            <Typography color='#333335' fontSize='16px' mb='19px' borderBottom='1px solid #F3F3F3' p='14px'>
                                Notifications
                            </Typography>
                            <Box display="flex" px='10px' alignItems="center" justifyContent='space-between' borderBottom='1px solid #F3F3F3' pb='11px' mb='10px'>
                                <Box display="flex">
                                    <Box borderRadius="50%" bgcolor="#26BF941A" height="40px" width="40px" display="flex" justifyContent="center" alignItems="center" marginRight="8px">
                                        <img src={greenTimer} alt="clock-img" />
                                    </Box>
                                    <Box>
                                        <Typography color='#232323' fontSize='14px' lineHeight='24px'>Plan Changed</Typography>
                                        <Typography color='#8C9097' fontSize='12px' lineHeight='18px'>Your plan has been changed to "Evergree..</Typography>
                                    </Box>
                                </Box>
                                <NotificationCancel />
                            </Box>
                            <Box display="flex" px='10px' alignItems="center" justifyContent='space-between' borderBottom='1px solid #F3F3F3' pb='11px' mb='10px'>
                                <Box display="flex">
                                    <Box borderRadius="50%" bgcolor="#E791BC1A" height="40px" width="40px" display="flex" justifyContent="center" alignItems="center" marginRight="8px">
                                        <img src={pinkTimer} alt="clock-img" />
                                    </Box>
                                    <Box>
                                        <Typography color='#232323' fontSize='14px' lineHeight='24px'>Subscription Expired</Typography>
                                        <Typography color='#8C9097' fontSize='12px' lineHeight='18px'>Your Subscription has expired</Typography>
                                    </Box>
                                </Box>
                                <NotificationCancel />
                            </Box>
                            <Box display="flex" px='10px' alignItems="center" justifyContent='space-between' borderBottom='1px solid #F3F3F3' pb='11px' mb='10px'>
                                <Box display="flex">
                                    <Box borderRadius="50%" bgcolor="#845ADF1A" height="40px" width="40px" display="flex" justifyContent="center" alignItems="center" marginRight="8px">
                                        <img src={purpleTimer} alt="clock-img" />
                                    </Box>
                                    <Box>
                                        <Typography color='#232323' fontSize='14px' lineHeight='24px'>Black Friday Deals</Typography>
                                        <Typography color='#8C9097' fontSize='12px' lineHeight='18px'>Get 50% off on all plans this season</Typography>
                                    </Box>
                                </Box>
                                <NotificationCancel />
                            </Box>
                            <Box p='16px' mt='105px' position='sticky' bottom='0' bgcolor='#FFf'>
                                <Button variant="contained" size='large' color='primary' disableElevation sx={{ width: '100%', textTransform: 'capitalize', backgroundColor: "#178EFC" }}>View All</Button>
                            </Box>
                        </Menu>
                    </Box>
                    <Box>
                        <IconButton onClick={handleMenuOpenProfile} sx={{ '&:hover': { bgcolor: '#fff' }, padding: '0', position: 'relative' }}>
                            <Avatar alt="" src={userInfo?.picture} />

                            {/* ---------------when user switch to sub account------------------- */}
                            {/* <Avatar alt="" src={userInfo?.picture} sx={{
                                position: 'absolute',
                                zIndex: 1,
                                height: '22px',
                                width: '22px',
                                border: '1px solid #FFFFFF',
                                bottom: -5,
                                right: -5
                            }} /> */}
                            {/* ---------------when user switch to sub account------------------- */}

                        </IconButton>
                        <Menu
                            anchorEl={anchorElProfile}
                            open={Boolean(anchorElProfile)}
                            onClose={handleMenuCloseProfile}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{ '& .MuiMenu-paper': { maxWidth: "300px", borderRadius: "12px !important", boxShadow: "0px 12px 20px 0px #0D0E213D" } }}
                        >

                            <Box p='17px' borderBottom='1px solid #F3F3F3'>
                                <Box display="flex" pb="8px">
                                    <Avatar alt={userInfo?.given_name || 'User'} src={userInfo?.picture} />
                                    <Box ml='16px' >
                                        <Box display="flex" alignContent="center">
                                            <Typography mb='4px' color='#0F0F0F' fontSize='16px'>
                                                {userInfo ? `${userInfo.name || ''}` : 'User Name'}
                                            </Typography>
                                            <Box p="0 12px" display="flex" color="#FFFFFF" bgcolor="#22B060" ml="12px" borderRadius="4px" fontSize="12px" height="19px">Active</Box>
                                        </Box>
                                        <Typography color='#666666' fontSize='16px' sx={{wordBreak: "break-all"}}>
                                            {userInfo?.email || ''}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* -------------------after switching to sub account----------------- */}
                                {/* <Box display="flex" alignItems="center" p='8px 12px' width='100%' bgcolor="#F3F6FF" sx={{ "&:hover": { bgcolor: '#E5ECFF', }, cursor: 'pointer' }} borderRadius="8px">
                                    <Avatar sx={{ height: "32px", width: "32px" }} alt={userInfo?.given_name || 'User'} src={userInfo?.picture} />
                                    <Tooltip title="Switch to Main Account" arrow slotProps={{
                                        tooltip: {
                                            sx: {
                                                backgroundColor: '#000',
                                                color: '#fff',
                                                '& .MuiTooltip-arrow': {
                                                    color: '#000',
                                                },
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '14px',
                                                padding: '0 16px',

                                            }
                                        },
                                    }}>
                                        <Box ml='10px' >
                                            <Box display="flex" alignContent="center">
                                                <Typography color='#0F0F0F' variant='body2'>
                                                    {userInfo ? `${userInfo.name || ''}` : 'User Name'}
                                                </Typography>
                                                <Box p="0 12px" display="flex" color="#666666" bgcolor="#FFFFFF" border="1px solid #A3A3A3" ml="12px" borderRadius="4px" fontSize="12px" height="19px">Main</Box>
                                            </Box>
                                            <Typography color='#666666' variant='body2'>
                                                {userInfo?.email || 'user@gmail.com'}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </Box> */}
                                {/* -------------------after switching to sub account----------------- */}

                            </Box>

                            <Box
                                display='flex'
                                p='8px 16px'
                                mt='8px'
                                alignItems='center'
                                sx={{ '&:hover': { bgcolor: '#F3F6F8' }, cursor: 'pointer' }}
                                onClick={handleManageProfile}
                            >
                                <ProfileIcon></ProfileIcon>
                                <Typography color='#0F0F0F' ml='16px' fontSize='14px'>Manage Profile</Typography>
                            </Box>
                            <Box
                                display='flex'
                                p='8px 16px'
                                alignItems='center'
                                sx={{ '&:hover': { bgcolor: '#F3F6F8' }, cursor: 'pointer' }}
                                onClick={() => window.open(`${config.easywebinar_url}/partners/`, '_blank')}  
                            >
                                <HandshakeIcon></HandshakeIcon>
                                <Typography color='#0F0F0F' ml='16px' fontSize='14px'>Become a Partner</Typography>
                            </Box>
                            <Box
                                display='flex'
                                p='8px 16px'
                                alignItems='center'
                                sx={{ '&:hover': { bgcolor: '#F3F6F8' }, cursor: 'pointer' }}
                                onClick={handleSignOut}
                            >
                                <SignOut></SignOut>
                                <Typography color='#0F0F0F' ml='16px' fontSize='14px'>Sign out</Typography>
                            </Box>
                        </Menu>
                    </Box>
                </Box>
            </Box>
        );
    }

    function DemoPageContent({ pathname, navigate, state }: DemoPageContentProps) {
        // NotFoundInDashboard component for 404s inside dashboard
        const NotFoundInDashboard = () => {
            const [countdown, setCountdown] = React.useState(5);
            
            const messages = [
                "We regret to inform you that this dashboard page is experiencing technical difficulties. The difficulty being it doesn't exist."
            ];
            
            const randomMessage = messages[Math.floor(Math.random() * messages.length)];
            
            React.useEffect(() => {
                const timer = setInterval(() => {
                    setCountdown((prevCount) => {
                        if (prevCount <= 1) {
                            clearInterval(timer);
                            navigate('/overview');
                            return 0;
                        }
                        return prevCount - 1;
                    });
                }, 1000);
                
                return () => clearInterval(timer);
            }, []);
            
            return (
                <ThemeProvider theme={theme}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '80vh',
                        textAlign: 'center',
                        padding: '20px'
                    }}>
                        <Typography variant="h2" color="primary" sx={{ mb: 3 }}>
                            404
                        </Typography>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                            {randomMessage}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4 }}>
                            We'll redirect you to the Overview page where you can continue your journey.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Redirecting in {countdown} seconds...
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => navigate('/overview')}
                            sx={{ textTransform: 'none' }}
                        >
                            Go to Overview Now
                        </Button>
                    </Box>
                </ThemeProvider>
            );
        };

        if (pathname === "/overview") {
            return <ThemeProvider theme={theme}><Overview navigate={navigate} /></ThemeProvider>;
        }
        else if (pathname === "/subscription") {
            // Security: Only OWNER role can access subscription management
            try {
                const permissionCheck = validateUserPermissions('OWNER');
                if (!permissionCheck.isValid) {
                    toast.error(permissionCheck.error || 'Access denied: Only account owners can manage subscriptions');
                    navigate('/dashboard/overview');
                    return <div>Redirecting...</div>;
                }
                return <ManageSubscription navigate={navigate} />;
            } catch (error) {
                console.error('Error validating subscription permissions:', error);
                toast.error('Error validating permissions');
                navigate('/dashboard/overview');
                return <div>Redirecting...</div>;
            }
        }
        else if (pathname === "/subscription/details") {
            // Security: Only OWNER role can access subscription details
            try {
                const permissionCheck = validateUserPermissions('OWNER');
                if (!permissionCheck.isValid) {
                    toast.error(permissionCheck.error || 'Access denied: Only account owners can view subscription details');
                    navigate('/dashboard/overview');
                    return <div>Redirecting...</div>;
                }
                return <ThemeProvider theme={theme}>
                    <SubscriptionDetails navigate={navigate} state={state} />
                </ThemeProvider>;
            } catch (error) {
                console.error('Error validating subscription details permissions:', error);
                toast.error('Error validating permissions');
                navigate('/dashboard/overview');
                return <div>Redirecting...</div>;
            }
        }
        // else if (pathname === "/subscription/1") {
        //     return <ThemeProvider theme={theme} ><SubscriptionDetails navigate={navigate} /></ThemeProvider>;
        // }
        // else if (pathname === "/subscription/2") {
        //     return <ThemeProvider theme={theme} ><SubscriptionDetailsEasyCRM navigate={navigate} /></ThemeProvider>;
        // }
        else if (pathname === "/billing") {
            return <ThemeProvider theme={theme} ><Billing /></ThemeProvider>;
        }
        else if (pathname === "/users") {
            return <ThemeProvider theme={theme} ><Users/></ThemeProvider>;
        }
        else if (pathname === "/workspaces") {
            return <ThemeProvider theme={theme} ><Workspaces /></ThemeProvider>;
        }
        else if (pathname === "/notifications") {
            return <ThemeProvider theme={theme}><Notifications /></ThemeProvider>;
        } 
        else if (pathname === "/settings") {
            return <ThemeProvider theme={theme}><Settings initialTab={state?.activeTab} /></ThemeProvider>;
        } 
        else {
            // Return the NotFoundInDashboard component for any unmatched routes
            return <NotFoundInDashboard />;
        }
    }

    const handleEasyCRMClick = () => {
        // Check entitlement before allowing access
        if (!crmEntitlement.isEnabled) {
            toast.error(crmEntitlement.message || 'Upgrade plan to access EasyCRM');
            return;
        }
        
        setIsAppLoading(true);
        const state = localStorage.getItem(STORAGE_KEYS.STATE)
        const easyCRMUrl = `${config.easycrm_url}/user/MaintainSession${state ? `?state=${state}` : ''}`;
        window.location.href = easyCRMUrl;
    }

    // Get the current path from the URL or default to '/overview'
    const initialPath = (() => {
        const fullPath = window.location.pathname;
        // If the path is just /dashboard, default to /overview
        if (fullPath === '/dashboard' || fullPath === '/dashboard/') {
            // Update URL to include /overview when it's just /dashboard
            window.history.replaceState({}, '', '/dashboard/overview');
            return '/overview';
        }
        // Otherwise, extract the part after /dashboard
        const match = fullPath.match(/^\/dashboard(\/.*)/);
        return match ? match[1] : '/overview';
    })();
    const router = useDemoRouter(initialPath);

    const demoTheme = createTheme({
        cssVariables: {},
        colorSchemes: { light: true }
    });

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(null);
    };


    return (
        <Box className={`dashboard-wrapper`}>
            {isLoading || isRedirecting ? (
                <Loader fullHeight minHeight='100vh' text='Redirecting to EasyWebinar...' fontSize='16px' />
            ) : (
                <AppProvider navigation={NAVIGATION} router={router as any} branding={{
                    logo: (
                        <>
                            <Tooltip
                                title="Apps"
                                arrow
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            '& .MuiTooltip-arrow': {
                                                color: '#000',
                                            },
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '14px',
                                            padding: '0 16px',

                                        }
                                    },
                                }}
                                className='tooltip'
                            >
                                <Box component="button" sx={{ display: 'flex', alignItems: 'center', height: '100%', cursor: "pointer", bgcolor: "transparent", ml: "13px", "&:hover": { "svg path": { fill: "#178EFC" }, bgcolor: "#f2f2f2" } }} border="1px solid transparent" borderRadius="10px" p="8px 12px" gap="14px" onClick={handleClick}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.48047 3.38473C2.48047 3.16031 2.56759 2.94509 2.72267 2.78641C2.87775 2.62772 3.08808 2.53857 3.30739 2.53857H4.96124C5.18055 2.53857 5.39088 2.62772 5.54596 2.78641C5.70104 2.94509 5.78816 3.16031 5.78816 3.38473V5.07704C5.78816 5.30145 5.70104 5.51667 5.54596 5.67536C5.39088 5.83404 5.18055 5.92319 4.96124 5.92319H3.30739C3.08808 5.92319 2.87775 5.83404 2.72267 5.67536C2.56759 5.51667 2.48047 5.30145 2.48047 5.07704V3.38473ZM2.48047 15.2309C2.48047 15.0065 2.56759 14.7912 2.72267 14.6326C2.87775 14.4739 3.08808 14.3847 3.30739 14.3847H4.96124C5.18055 14.3847 5.39088 14.4739 5.54596 14.6326C5.70104 14.7912 5.78816 15.0065 5.78816 15.2309V16.9232C5.78816 17.1476 5.70104 17.3628 5.54596 17.5215C5.39088 17.6802 5.18055 17.7693 4.96124 17.7693H3.30739C3.08808 17.7693 2.87775 17.6802 2.72267 17.5215C2.56759 17.3628 2.48047 17.1476 2.48047 16.9232V15.2309ZM14.8843 2.53857C14.665 2.53857 14.4547 2.62772 14.2996 2.78641C14.1445 2.94509 14.0574 3.16031 14.0574 3.38473V5.07704C14.0574 5.30145 14.1445 5.51667 14.2996 5.67536C14.4547 5.83404 14.665 5.92319 14.8843 5.92319H16.5382C16.7575 5.92319 16.9678 5.83404 17.1229 5.67536C17.278 5.51667 17.3651 5.30145 17.3651 5.07704V3.38473C17.3651 3.16031 17.278 2.94509 17.1229 2.78641C16.9678 2.62772 16.7575 2.53857 16.5382 2.53857H14.8843ZM14.0574 9.30781C14.0574 9.08339 14.1445 8.86817 14.2996 8.70948C14.4547 8.5508 14.665 8.46165 14.8843 8.46165H16.5382C16.7575 8.46165 16.9678 8.5508 17.1229 8.70948C17.278 8.86817 17.3651 9.08339 17.3651 9.30781V11.0001C17.3651 11.2245 17.278 11.4397 17.1229 11.5984C16.9678 11.7571 16.7575 11.8463 16.5382 11.8463H14.8843C14.665 11.8463 14.4547 11.7571 14.2996 11.5984C14.1445 11.4397 14.0574 11.2245 14.0574 11.0001V9.30781ZM9.09585 8.46165C8.87654 8.46165 8.66621 8.5508 8.51113 8.70948C8.35605 8.86817 8.26893 9.08339 8.26893 9.30781V11.0001C8.26893 11.2245 8.35605 11.4397 8.51113 11.5984C8.66621 11.7571 8.87654 11.8463 9.09585 11.8463H10.7497C10.969 11.8463 11.1793 11.7571 11.3344 11.5984C11.4895 11.4397 11.5766 11.2245 11.5766 11.0001V9.30781C11.5766 9.08339 11.4895 8.86817 11.3344 8.70948C11.1793 8.5508 10.969 8.46165 10.7497 8.46165H9.09585ZM2.48047 9.30781C2.48047 9.08339 2.56759 8.86817 2.72267 8.70948C2.87775 8.5508 3.08808 8.46165 3.30739 8.46165H4.96124C5.18055 8.46165 5.39088 8.5508 5.54596 8.70948C5.70104 8.86817 5.78816 9.08339 5.78816 9.30781V11.0001C5.78816 11.2245 5.70104 11.4397 5.54596 11.5984C5.39088 11.7571 5.18055 11.8463 4.96124 11.8463H3.30739C3.08808 11.8463 2.87775 11.7571 2.72267 11.5984C2.56759 11.4397 2.48047 11.2245 2.48047 11.0001V9.30781ZM9.09585 2.53857C8.87654 2.53857 8.66621 2.62772 8.51113 2.78641C8.35605 2.94509 8.26893 3.16031 8.26893 3.38473V5.07704C8.26893 5.30145 8.35605 5.51667 8.51113 5.67536C8.66621 5.83404 8.87654 5.92319 9.09585 5.92319H10.7497C10.969 5.92319 11.1793 5.83404 11.3344 5.67536C11.4895 5.51667 11.5766 5.30145 11.5766 5.07704V3.38473C11.5766 3.16031 11.4895 2.94509 11.3344 2.78641C11.1793 2.62772 10.969 2.53857 10.7497 2.53857H9.09585ZM8.26893 15.2309C8.26893 15.0065 8.35605 14.7912 8.51113 14.6326C8.66621 14.4739 8.87654 14.3847 9.09585 14.3847H10.7497C10.969 14.3847 11.1793 14.4739 11.3344 14.6326C11.4895 14.7912 11.5766 15.0065 11.5766 15.2309V16.9232C11.5766 17.1476 11.4895 17.3628 11.3344 17.5215C11.1793 17.6802 10.969 17.7693 10.7497 17.7693H9.09585C8.87654 17.7693 8.66621 17.6802 8.51113 17.5215C8.35605 17.3628 8.26893 17.1476 8.26893 16.9232V15.2309ZM14.8843 14.3847C14.665 14.3847 14.4547 14.4739 14.2996 14.6326C14.1445 14.7912 14.0574 15.0065 14.0574 15.2309V16.9232C14.0574 17.1476 14.1445 17.3628 14.2996 17.5215C14.4547 17.6802 14.665 17.7693 14.8843 17.7693H16.5382C16.7575 17.7693 16.9678 17.6802 17.1229 17.5215C17.278 17.3628 17.3651 17.1476 17.3651 16.9232V15.2309C17.3651 15.0065 17.278 14.7912 17.1229 14.6326C16.9678 14.4739 16.7575 14.3847 16.5382 14.3847H14.8843Z" fill="#666666" />
                                    </svg>
                                </Box>
                            </Tooltip>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button',
                                }}
                                sx={{
                                    '& .MuiMenu-paper': { borderRadius: "12px", boxShadow: "0px 12px 20px 0px #0D0E213D" }, "& ul": {
                                        display: "flex",
                                        p: "16px",
                                        "& li": {
                                            padding: "13px",
                                            borderRadius: "8px",
                                            border: "1px solid #F3F3F3",
                                            width: "106px",
                                            "& > div": {
                                                justifyContent: "center",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                            }
                                        }
                                    }
                                }}
                                onClick={(e)=> {e.preventDefault(); e.stopPropagation();}}
                            >
                                <MenuItem onClick={handleEasyWebinarClick} disabled={isEasyWebinarLoading} sx={{ "&.Mui-disabled": { opacity: isEasyWebinarLoading ? 1 : 1 } }}>
                                    <Box width="100%" position="relative">
                                        {isEasyWebinarLoading && (
                                            <Loader sx={{ position: 'fixed', zIndex: 999999, top: 0, right: 0, left: 0, bottom: 0 }} fullHeight minHeight='100vh' text='Launching EasyWebinar...' fontSize='16px' />
                                        )}
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easywebinar-logo.png" alt="easywebinar" />
                                        <Box mt="10px" display="flex"><img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easywebinar-text-logo.svg" alt="easywebinar" /></Box>
                                    </Box>
                                </MenuItem>
                                
                                <MenuItem 
                                    onClick={() => {
                                        if (crmEntitlement.isEnabled) {
                                            handleEasyCRMClick();
                                        } else {
                                            toast.error(crmEntitlement.message || 'Upgrade plan to access EasyCRM');
                                        }
                                    }}
                                    disabled={isAppLoading}
                                    sx={{ 
                                        "&": { marginLeft: "10px", position: "relative" }, 
                                        "&.Mui-disabled": { opacity: isAppLoading ? 1 : 1 },
                                        opacity: crmEntitlement.isEnabled ? 1 : 0.7,
                                        cursor: crmEntitlement.isEnabled ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    <Box width="100%" position="relative">
                                        {isAppLoading && (
                                            <Loader sx={{ position: 'fixed', zIndex: 999999, top: 0, right: 0, left: 0, bottom: 0 }} fullHeight minHeight='100vh' text='Launching EasyCRM...' fontSize='16px' />
                                        )}
                                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easycrm-logo.png" alt="easycrm" />
                                        <Box mt="10px" display="flex"><img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/easycrm-text-logo.svg" alt="easycrm" /></Box>
                                        {!crmEntitlement.isEnabled && (
                                            <Tooltip title="Upgrade plan to access EasyCRM" arrow>
                                                <Box position="absolute" top="8px" right="6px">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                        <path d="M9 9.75C8.77166 9.74733 8.54799 9.81468 8.35908 9.94298C8.17018 10.0713 8.02511 10.2544 7.94341 10.4676C7.86171 10.6808 7.8473 10.914 7.90211 11.1357C7.95693 11.3573 8.07834 11.5569 8.25 11.7075V12.75C8.25 12.9489 8.32902 13.1397 8.46967 13.2803C8.61032 13.421 8.80109 13.5 9 13.5C9.19891 13.5 9.38968 13.421 9.53033 13.2803C9.67098 13.1397 9.75 12.9489 9.75 12.75V11.7075C9.92166 11.5569 10.0431 11.3573 10.0979 11.1357C10.1527 10.914 10.1383 10.6808 10.0566 10.4676C9.97489 10.2544 9.82982 10.0713 9.64092 9.94298C9.45201 9.81468 9.22834 9.74733 9 9.75ZM12.75 6.75V5.25C12.75 4.25544 12.3549 3.30161 11.6517 2.59835C10.9484 1.89509 9.99456 1.5 9 1.5C8.00544 1.5 7.05161 1.89509 6.34835 2.59835C5.64509 3.30161 5.25 4.25544 5.25 5.25V6.75C4.65326 6.75 4.08097 6.98705 3.65901 7.40901C3.23705 7.83097 3 8.40326 3 9V14.25C3 14.8467 3.23705 15.419 3.65901 15.841C4.08097 16.2629 4.65326 16.5 5.25 16.5H12.75C13.3467 16.5 13.919 16.2629 14.341 15.841C14.7629 15.419 15 14.8467 15 14.25V9C15 8.40326 14.7629 7.83097 14.341 7.40901C13.919 6.98705 13.3467 6.75 12.75 6.75ZM6.75 5.25C6.75 4.65326 6.98705 4.08097 7.40901 3.65901C7.83097 3.23705 8.40326 3 9 3C9.59674 3 10.169 3.23705 10.591 3.65901C11.0129 4.08097 11.25 4.65326 11.25 5.25V6.75H6.75V5.25ZM13.5 14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H5.25C5.05109 15 4.86032 14.921 4.71967 14.7803C4.57902 14.6397 4.5 14.4489 4.5 14.25V9C4.5 8.80109 4.57902 8.61032 4.71967 8.46967C4.86032 8.32902 5.05109 8.25 5.25 8.25H12.75C12.9489 8.25 13.1397 8.32902 13.2803 8.46967C13.421 8.61032 13.5 8.80109 13.5 9V14.25Z" fill="#666666" />
                                                    </svg>
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </MenuItem>

                            </Menu>
                        </>),
                    title: '',
                }} theme={demoTheme} >
                    <DashboardLayout
                        disableCollapsibleSidebar
                        sidebarExpandedWidth="96px"
                        slots={{
                            toolbarActions: () => <ToolbarActionsSearch navigate={router.navigate} />,
                        }}
                        sx={
                            { background: '#FAFAFA', }
                        }
                    >
                        <PageContainer breadcrumbs={[]} title='' sx={{
                            maxWidth: { lg: "1503px" }, "& nav[aria-label='breadcrumb']": {
                                display: "none"
                            }
                        }}>
                            {/* Subscription Banner - Shows on all dashboard pages except subscription page */}
                            {/* {router.pathname !== "/subscription" && (
                                <Box 
                                    sx={{ 
                                        width: '100%', 
                                        mt: 1, 
                                        mb: 2, 
                                        display: 'flex', 
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => router.navigate('/subscription')}
                                >
                                    <img
                                        src="https://easywebinar-site.easywebinar.com/images/all-pages-banner.png"
                                        alt="Subscription Banner"
                                        fetchPriority="high"
                                        style={{ width: '100%', maxWidth: 1600, borderRadius: 12, boxShadow: '0px 4px 24px 0px #2B2B3040' }}
                                    />
                                </Box>
                            )} */}
                            <DemoPageContent pathname={router.pathname} navigate={router.navigate} state={router.state} />
                        </PageContainer>
                    </DashboardLayout>
                </AppProvider >
            )}
        </Box >
    );
};

export default Dashboard;
